/*
  <free-shipping-bar> custom element.
  Subscribes to PUB_SUB_EVENTS.cartUpdate (defined globally in constants.js +
  pubsub.js, loaded ahead of this file in layout/theme.liquid) and re-renders
  the progress bar fill + copy whenever the cart total changes.

  Three contexts:
    drawer      — slim bar inside cart drawer
    cart        — bar on /cart page
    announcement— text-only message inside the utility-bar announcement slot
*/

class FreeShippingBar extends HTMLElement {
  constructor() {
    super();
    this.unsubscribe = null;
  }

  connectedCallback() {
    this.threshold = Number(this.dataset.threshold) || 0;
    this.context = this.dataset.context || 'cart';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.emptyText = this.dataset.emptyText || '';
    this.unlockedText = this.dataset.unlockedText || "You’ve unlocked free US shipping!";
    this.fill = this.querySelector('[data-fs-fill]');
    this.track = this.querySelector('.fs-bar__track');
    this.label = this.querySelector('.fs-bar__label');
    this.percentBadge = this.querySelector('[data-fs-percent]');
    this.announcementTrack = this.querySelector('.fs-bar__track--announcement');

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const total = event && event.cartData && typeof event.cartData.total_price === 'number'
          ? event.cartData.total_price
          : null;
        if (total === null) return;
        this.render(total);
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  render(totalCents) {
    if (!this.threshold) return;
    const remaining = Math.max(0, this.threshold - totalCents);
    const percent = Math.min(100, Math.round((totalCents / this.threshold) * 100));
    const unlocked = totalCents >= this.threshold;
    const empty = totalCents <= 0;

    if (this.fill) this.fill.style.width = percent + '%';
    if (this.track) this.track.setAttribute('aria-valuenow', String(percent));
    if (this.percentBadge) this.percentBadge.textContent = percent + '%';
    if (this.announcementTrack) {
      this.announcementTrack.setAttribute('aria-hidden', empty ? 'true' : 'false');
    }

    this.classList.toggle('fs-bar--unlocked', unlocked);

    if (!this.label) return;

    if (this.context === 'announcement') {
      if (empty) {
        this.label.textContent = this.emptyText;
      } else if (unlocked) {
        this.label.textContent = '✓ ' + this.unlockedText;
      } else {
        const amountStr = this.formatMoney(remaining);
        this.label.innerHTML =
          'Add <span class="fs-bar__amount" data-fs-amount>' +
          amountStr +
          '</span> more for free US shipping';
      }
      return;
    }

    if (unlocked) {
      this.label.textContent = this.unlockedText;
    } else {
      const amountStr = this.formatMoney(remaining);
      this.label.innerHTML =
        'Add <span class="fs-bar__amount" data-fs-amount>' +
        amountStr +
        '</span> more for free US shipping';
    }
  }

  formatMoney(cents) {
    if (typeof window !== 'undefined' && typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }
    const value = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return '$' + value;
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
